import { tokens } from './tokens.js';

/**
 * Publishes the design tokens to CSS as custom properties.
 *
 * The stylesheets need the values and so does the occasional inline style. This
 * keeps `tokens.ts` the single definition rather than having a palette in
 * TypeScript and a second, slowly diverging one in CSS.
 *
 * The naming is mechanical — `color.amberHigh` becomes `--kh-color-amber-high` —
 * so a token added to the object appears in CSS without a second edit.
 */
const toKebabCase = (value: string): string =>
  value.replace(/[A-Z0-9]+/g, (match) => `-${match.toLowerCase()}`);

export const themeVariables = (): Record<string, string> =>
  Object.entries(tokens)
    .flatMap(([group, entries]) =>
      Object.entries(entries).map(
        ([name, value]) => [`--kh-${group}-${toKebabCase(name)}`, value] as const,
      ),
    )
    .reduce<Record<string, string>>((accumulator, [name, value]) => {
      accumulator[name] = value;
      return accumulator;
    }, {});

/** Applied once, before the first render. */
export const applyTheme = (root: HTMLElement): void => {
  for (const [name, value] of Object.entries(themeVariables())) {
    root.style.setProperty(name, value);
  }
};
